import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchHierarchy } from '../../api/analytics';
import type { HierarchyNode, HierarchyResponse } from '../../api/analytics';
import { OrgStatusCard } from './OrgStatusCard';
import { ChartCard } from '../charts/ChartCard';
import { CostDonutChart } from '../charts/CostDonutChart';
import { ComparisonBarChart } from '../charts/ComparisonBarChart';
import { buildCostDonutData } from '../charts/chartTheme';
import { fmt, fmtCHF, fmtM } from '../../utils/format';
import { getCantonName } from '../../utils/cantonData';
import styles from './OrgOverviewSection.module.css';

interface BreadcrumbItem {
  id: string | null;
  name: string;
}

function levelLabel(level: string): string {
  if (level === 'federal') return 'Federal';
  if (level === 'canton') return 'Cantons';
  if (level === 'commune') return 'Communes';
  return level;
}

function badgeClass(level: string): string {
  if (level === 'federal') return styles.badgeFederal ?? '';
  if (level === 'canton') return styles.badgeCanton ?? '';
  if (level === 'commune') return styles.badgeCommune ?? '';
  return '';
}

function childLevelOf(level: string): string {
  if (level === 'federal') return 'canton';
  if (level === 'canton') return 'commune';
  return '';
}

export function OrgOverviewSection() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [hierarchy, setHierarchy] = useState<HierarchyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Navigation state: which node is currently expanded
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
    { id: null, name: 'Overview' },
  ]);
  const [currentNodes, setCurrentNodes] = useState<HierarchyNode[]>([]);
  const [selectedNode, setSelectedNode] = useState<HierarchyNode | null>(null);

  useEffect(() => {
    loadHierarchy();
  }, []);

  async function loadHierarchy() {
    try {
      setLoading(true);
      setError('');
      const data = await fetchHierarchy();
      setHierarchy(data);
      setCurrentNodes(data.roots);
    } catch {
      setError('Failed to load organizational data');
    } finally {
      setLoading(false);
    }
  }

  function nodeDisplayName(node: { name: string; level: string; code: string }): string {
    if (node.level === 'canton' && node.code) return getCantonName(node.code, i18n.language);
    return node.name;
  }

  const handleDrillDown = useCallback((node: HierarchyNode) => {
    if (node.children.length > 0) {
      // Drill into this node — show its children
      setSelectedNode(node);
      setCurrentNodes(node.children);
      setBreadcrumbs((prev) => [...prev, { id: node.id, name: nodeDisplayName(node) }]);
    } else {
      // Leaf node — navigate to analytics page
      navigate(`/app/analytics/${node.id}`);
    }
  }, [navigate, i18n.language]);

  const handleBreadcrumbClick = useCallback((index: number) => {
    if (!hierarchy) return;
    if (index === 0) {
      // Back to root
      setSelectedNode(null);
      setCurrentNodes(hierarchy.roots);
      setBreadcrumbs([{ id: null, name: 'Overview' }]);
    } else {
      // Navigate to a specific breadcrumb level
      const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
      setBreadcrumbs(newBreadcrumbs);

      // Walk the tree to find the correct nodes
      let nodes = hierarchy.roots;
      let selected: HierarchyNode | null = null;
      for (let i = 1; i < newBreadcrumbs.length; i++) {
        const crumb = newBreadcrumbs[i]!;
        const found = nodes.find((n) => n.id === crumb.id);
        if (found) {
          selected = found;
          nodes = found.children;
        }
      }
      setSelectedNode(selected);
      setCurrentNodes(nodes);
    }
  }, [hierarchy, breadcrumbs]);

  if (loading) {
    return <div className={styles.loading}>Loading organizational overview...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (!hierarchy || hierarchy.roots.length === 0) {
    return (
      <div className={styles.empty}>
        <h3>No organizations configured</h3>
        <p>Set up your organizational hierarchy to see the overview.</p>
      </div>
    );
  }

  // Determine the child level label
  const childLevel = selectedNode ? childLevelOf(selectedNode.level) : '';
  const childLevelStr = childLevel ? levelLabel(childLevel) : '';

  // Chart data from current level
  const barData = currentNodes.map((n) => {
    const dn = nodeDisplayName(n);
    return {
      name: dn.length > 15 ? dn.slice(0, 15) + '...' : dn,
      logements: n.total_logements,
      emplois: n.total_emplois,
    };
  });

  const donutNode = selectedNode || (hierarchy.roots.length === 1 ? hierarchy.roots[0] : null);
  const donutData = donutNode ? buildCostDonutData(donutNode.cost_breakdown) : [];
  const donutTotal = donutNode?.total_cost ?? 0;

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Organizational Overview</h2>
        {selectedNode && (
          <button
            className={styles.viewAllBtn}
            onClick={() => navigate(`/app/analytics/${selectedNode.id}`)}
          >
            Full Analytics &rarr;
          </button>
        )}
      </div>

      {/* Breadcrumb */}
      {breadcrumbs.length > 1 && (
        <div className={styles.breadcrumb}>
          {breadcrumbs.map((crumb, i) => (
            <span key={crumb.id ?? 'root'}>
              {i > 0 && <span className={styles.breadcrumbSep}>/</span>}
              {i < breadcrumbs.length - 1 ? (
                <button
                  className={styles.breadcrumbItem}
                  onClick={() => handleBreadcrumbClick(i)}
                >
                  {crumb.name}
                </button>
              ) : (
                <span className={styles.breadcrumbCurrent}>
                  {crumb.name}
                </span>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Selected node detail panel */}
      {selectedNode && (
        <div className={styles.detailPanel}>
          <div className={styles.detailHeader}>
            <span className={styles.detailName}>{nodeDisplayName(selectedNode)}</span>
            <span className={`${styles.detailBadge} ${badgeClass(selectedNode.level)}`}>
              {selectedNode.level}
            </span>
          </div>
          <div className={styles.detailStats}>
            <div className={styles.detailStat}>
              <span className={styles.detailStatValue}>{fmt(selectedNode.total_logements)}</span>
              <span className={styles.detailStatLabel}>Logements</span>
            </div>
            <div className={styles.detailStat}>
              <span className={styles.detailStatValue}>{fmt(selectedNode.total_emplois)}</span>
              <span className={styles.detailStatLabel}>Emplois</span>
            </div>
            <div className={styles.detailStat}>
              <span className={styles.detailStatValue}>{selectedNode.project_count}</span>
              <span className={styles.detailStatLabel}>Projects</span>
            </div>
            <div className={styles.detailStat}>
              <span className={styles.detailStatValue}>
                {selectedNode.total_cost > 0 ? fmtCHF(selectedNode.total_cost) : '\u2014'}
              </span>
              <span className={styles.detailStatLabel}>Investment</span>
            </div>
            <div className={styles.detailStat}>
              <span className={styles.detailStatValue}>
                {selectedNode.avg_density > 0 ? selectedNode.avg_density : '\u2014'}
              </span>
              <span className={styles.detailStatLabel}>Density (log/ha)</span>
            </div>
          </div>
        </div>
      )}

      {/* Children label */}
      {currentNodes.length > 0 && (
        <div className={styles.childrenLabel}>
          {selectedNode
            ? `${childLevelStr} (${currentNodes.length})`
            : `Organizations (${currentNodes.length})`
          }
        </div>
      )}

      {/* Clickable status cards */}
      <div className={styles.cardsGrid}>
        {currentNodes.map((node) => (
          <OrgStatusCard key={node.id} node={node} onClick={handleDrillDown} />
        ))}
      </div>

      {/* Visual analysis charts */}
      {currentNodes.length > 1 && (
        <div className={styles.chartsRow}>
          <ChartCard
            title={`${childLevelStr || 'Organizations'} Comparison`}
            subtitle="Logements & Emplois"
          >
            <ComparisonBarChart
              data={barData}
              metrics={[
                { key: 'logements', label: 'Logements', color: '#6366f1' },
                { key: 'emplois', label: 'Emplois', color: '#22d3ee' },
              ]}
              layout={currentNodes.length > 5 ? 'vertical' : 'horizontal'}
            />
          </ChartCard>
          {donutData.length > 0 && donutTotal > 0 && (
            <ChartCard
              title="Cost Breakdown"
              subtitle={selectedNode ? nodeDisplayName(selectedNode) : 'All organizations'}
            >
              <CostDonutChart
                data={donutData}
                centerValue={fmtM(donutTotal)}
                centerLabel="Total"
              />
            </ChartCard>
          )}
        </div>
      )}
    </div>
  );
}
